import type { ReactNode } from 'react';
import styles from '../ControlBridge.module.css';

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className={styles.sectionLabel}>{children}</div>;
}

export function EmptyState({ label }: { label: string }) {
  return <div className={styles.emptyState}>Nothing in this view for {label}.</div>;
}

export function NotAssessed({ label }: { label: string }) {
  return (
    <div className={styles.notAssessed} role="status">
      <strong>{label}</strong> — not yet assessed in the live model. This section will populate when
      workspace derivation exposes the required fields.
    </div>
  );
}

export function AlertBanner({
  title,
  detail,
  children,
}: {
  title: string;
  detail?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.alertBanner}>
      <span className={styles.alertTitle} role="alert">
        {title}
      </span>
      {detail ? <span className={styles.alertDetail}>{detail}</span> : null}
      {children}
    </div>
  );
}

export function MetricTile({
  label,
  value,
  sub,
  meaning,
  onClick,
  selected,
}: {
  label: string;
  value: string;
  sub?: string;
  meaning?: 'good' | 'warn' | 'alert' | 'muted';
  onClick?: () => void;
  selected?: boolean;
}) {
  const className = `${styles.metricTile} ${onClick ? styles.metricTileClickable : ''} ${selected ? styles.metricTileSelected : ''}`;
  const inner = (
    <>
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.metricValue} data-meaning={meaning ?? 'none'}>
        {value}
      </div>
      {sub ? <div className={styles.metricSub}>{sub}</div> : null}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <div className={className}>{inner}</div>;
}

export function NavBlock({
  current,
  onChange,
}: {
  current: string;
  onChange: (surface: string) => void;
}) {
  return (
    <div className={styles.navBlock}>
      <span className={styles.navBlockLabel}>Other surfaces</span>
      <div className={styles.navBlockLinks}>
        {[
          ['mission', 'Mission Control'],
          ['drive', 'Drive'],
          ['guidance', 'Guidance'],
          ['diagnose', 'Diagnose'],
          ['exopanel', 'ExoPanel'],
        ]
          .filter(([key]) => key !== current)
          .map(([key, label]) => (
            <button key={key} type="button" className={styles.navLink} onClick={() => onChange(key)}>
              {label}
            </button>
          ))}
      </div>
    </div>
  );
}

export function FilterBar({
  scopeLabel,
  resourceLabel,
  onClearResource,
  sessionKindLabel,
}: {
  scopeLabel: string;
  resourceLabel: string;
  onClearResource?: () => void;
  sessionKindLabel?: string;
}) {
  return (
    <div className={styles.filterBar}>
      {sessionKindLabel ? (
        <>
          <span data-testid="cb-session-kind">
            Session: <strong>{sessionKindLabel}</strong>
          </span>
          <span aria-hidden="true"> · </span>
        </>
      ) : null}
      <span>
        Viewing: <strong>{scopeLabel}</strong>
      </span>
      <span aria-hidden="true"> · </span>
      <span>
        Resource: <strong>{resourceLabel}</strong>
        {onClearResource && resourceLabel !== 'All resources' ? (
          <button type="button" className={styles.clearFilter} onClick={onClearResource}>
            Clear
          </button>
        ) : null}
      </span>
    </div>
  );
}
